import React, { useState } from "react";

/** Panneau admin : générateur de posts sociaux via Gemini */
export default function AiAssistantAdminPanel({ API, pinInput }) {
  const [keywords, setKeywords] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [tone, setTone] = useState("chaleureux");
  const [lang, setLang] = useState("fr");
  const [busy, setBusy] = useState(false);
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState("");

  const generate = async () => {
    if (!keywords.trim()) { setError("Ajoutez au moins 3 mots-clés."); return; }
    if (!pinInput) { setError("PIN manquant."); return; }
    setBusy(true); setError(""); setPosts([]);
    try {
      const r = await fetch(`${API}/ai/generate-social-post`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin: pinInput, keywords, platform, tone, lang }),
      });
      if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`);
      const d = await r.json();
      setPosts(d.posts || []);
    } catch (e) { setError(`Erreur : ${e.message}`); }
    finally { setBusy(false); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
  };

  return (
    <div data-testid="ai-assistant-panel">
      <h3 className="font-heading text-xl font-bold text-gray-900 mb-1">
        🤖 Assistant IA — Générateur de posts sociaux
      </h3>
      <p className="text-sm text-gray-600 mb-5">
        Propulsé par Gemini 3.1 Pro. Donnez 3-8 mots-clés → recevez 3 posts prêts à copier-coller
        (émotionnel, factuel, appel à l&apos;action). Utile pour Instagram, WhatsApp, Facebook, Twitter/X.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-bold mb-1" htmlFor="ai-keywords">
            Mots-clés (thème, événement, sujet)
          </label>
          <textarea
            id="ai-keywords"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            rows={3}
            placeholder="ex : gala 8 septembre + inscriptions académie + Atakpamé + mémoire Safuayé"
            data-testid="ai-keywords"
            className="w-full border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:border-[#0B2545] focus:outline-none"
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs font-bold mb-1">Plateforme</label>
            <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                    data-testid="ai-platform"
                    className="w-full border-2 border-gray-300 rounded-lg px-2 py-2 text-sm">
              <option value="instagram">📸 Instagram</option>
              <option value="whatsapp">💬 WhatsApp</option>
              <option value="facebook">📘 Facebook</option>
              <option value="twitter">🐦 Twitter/X</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold mb-1">Ton</label>
            <select value={tone} onChange={(e) => setTone(e.target.value)}
                    data-testid="ai-tone"
                    className="w-full border-2 border-gray-300 rounded-lg px-2 py-2 text-sm">
              <option value="chaleureux">Chaleureux</option>
              <option value="officiel">Officiel</option>
              <option value="jeune">Jeune / punchy</option>
              <option value="reconnaissant">Reconnaissant</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold mb-1">Langue</label>
            <select value={lang} onChange={(e) => setLang(e.target.value)}
                    data-testid="ai-lang"
                    className="w-full border-2 border-gray-300 rounded-lg px-2 py-2 text-sm">
              <option value="fr">🇫🇷 Français</option>
              <option value="en">🇬🇧 Anglais</option>
              <option value="it">🇮🇹 Italien</option>
            </select>
          </div>
        </div>
      </div>

      <button
        onClick={generate}
        disabled={busy}
        data-testid="ai-generate-btn"
        className="px-6 py-3 bg-[#0B2545] hover:bg-[#08192F] disabled:bg-gray-300 text-white font-bold rounded-lg shadow"
      >
        {busy ? "⏳ Génération…" : "🚀 Générer 3 posts"}
      </button>

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg" data-testid="ai-error">
          {error}
        </div>
      )}

      {posts.length > 0 && (
        <div className="mt-6 space-y-4" data-testid="ai-posts-result">
          {posts.map((p, i) => (
            <div key={i} className="border-2 border-amber-200 rounded-xl overflow-hidden shadow-sm">
              <div className="bg-amber-50 px-4 py-2 flex items-center justify-between">
                <span className="font-bold text-amber-900 text-sm">
                  Variante {p.variant} · {p.text.length} caractères
                </span>
                <button
                  onClick={() => copy(p.text)}
                  data-testid={`ai-copy-${p.variant}`}
                  className="text-xs bg-white border border-amber-400 text-amber-800 px-3 py-1 rounded hover:bg-amber-100 font-semibold"
                >
                  📋 Copier
                </button>
              </div>
              <div className="p-4 text-sm text-gray-800 whitespace-pre-wrap leading-relaxed bg-white">
                {p.text}
              </div>
            </div>
          ))}
          <p className="text-xs text-gray-500 italic">
            💡 Astuce : mélangez les 3 versions selon le canal (émotionnel sur Insta,
            factuel sur Facebook, appel à l&apos;action sur WhatsApp).
          </p>
        </div>
      )}
    </div>
  );
}
